import React from "react"
import { useDispatch } from "react-redux"
import { useEffect, useState } from "react"
import sortBy from "lodash/sortBy"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import {
  TAdeleteSmlManagers,
  TAgetSmlManagers,
  TAmanagerReport,
} from "../../../services/managerAPI"
import { SMLManager } from "../../../types/smlManager"
import { columnConfig } from "./components/columns"
import CreateManagerModal from "./components/CreateManagerModal"
import UpdateManagerModal from "./components/UpdateManagerModal"
import { useNavigate } from "react-router-dom"
import StickyDataTable from "../../../components/StickyDataTable"
import { getErrorStatus, getErrorResponse } from "../../../types/error"

const SmlManager = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [managers, setManagers] = useState<SMLManager[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZES[2])
  const [totalCount, setTotalCount] = useState(0)
  const [initialRecords, setInitialRecords] = useState(sortBy(managers, "id"))
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isReportLoading, setIsReportLoading] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [selectedManager, setSelectedManager] = useState<SMLManager | null>(null)
  const [sortByColumn, setSortByColumn] = useState<string>("")
  const [sortOrder, setSortOrder] = useState<boolean>(false)

  useEffect(() => {
    dispatch(setPageTitle("SML Manager"))
  }, [dispatch])

  useEffect(() => {
    handleFetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortByColumn, sortOrder])

  const handleFetchData = async () => {
    setIsLoading(true)

    try {
      const response = await TAgetSmlManagers(page, pageSize, sortByColumn, sortOrder)
      if (response !== undefined) {
        const indexedManagers = response.managers.map((manager: SMLManager) => ({
          ...manager,
          id: manager._id,
        }))
        setManagers(indexedManagers)
        setInitialRecords(indexedManagers)
        setTotalCount(response.totalCount)
      } else {
        setError("No data found")
      }
      setIsLoading(false)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("No data found")
    }
  }

  const deleteManager = async (_id: string) => {
    setIsLoading(true)
    try {
      const response = await TAdeleteSmlManagers(_id)
      if (response !== undefined) {
        const updatedManagers = managers.filter((manager) => String(manager._id) !== String(_id))
        const indexedManagers = updatedManagers.map((manager) => ({
          ...manager,
          id: manager._id,
        }))
        setManagers(indexedManagers)
        setInitialRecords(indexedManagers)
        setTotalCount(updatedManagers.length)
      } else {
        setError("No data found")
      }
      setIsLoading(false)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("No data found")
    } finally {
      setIsLoading(false)
    }
  }

  const handleReport = async (analysisId: string) => {
    setIsReportLoading(true)
    try {
      const blob = await TAmanagerReport(analysisId)

      const url = window.URL.createObjectURL(blob)

      const a = document.createElement("a")
      a.href = url
      a.download = `report_${analysisId}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()

      window.URL.revokeObjectURL(url)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error downloading report, ${getErrorResponse(error) || "Unknown error"}`)
    } finally {
      setIsReportLoading(false)
    }
  }

  const columns = columnConfig({
    deleteManager,
    setSelectedManager,
    setIsUpdateModalOpen,
    handleReport,
    isReportLoading,
  })

  const handleSorting = (columnAccessor: string, direction?: "asc" | "desc") => {
    if (columnAccessor === sortByColumn) {
      setSortOrder(direction === "desc")
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(direction === "desc")
    }
  }

  return (
    <div className="panel">
      <div className="datatables">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">SML Manager</h2>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md shadow transition-all duration-200"
                onClick={() => {
                  setIsCreateModalOpen(true)
                }}
              >
                Manager Oluştur
              </button>
            </div>
            <CreateManagerModal
              isOpen={isCreateModalOpen}
              onClose={() => setIsCreateModalOpen(false)}
            />
            <UpdateManagerModal
              manager={selectedManager}
              isOpen={isUpdateModalOpen}
              onClose={() => setIsUpdateModalOpen(false)}
            />
            <StickyDataTable
              highlightOnHover={true}
              className="whitespace-nowrap"
              records={initialRecords}
              columns={columns}
              totalRecords={totalCount}
              recordsPerPage={pageSize}
              page={page}
              onPageChange={(p) => setPage(p)}
              recordsPerPageOptions={PAGE_SIZES}
              onRecordsPerPageChange={setPageSize}
              sortStatus={{
                columnAccessor: sortByColumn,
                direction: sortOrder ? "desc" : "asc",
              }}
              onSortStatusChange={(e) => handleSorting(e.columnAccessor, e.direction)}
              minHeight={200}
              paginationText={({ from, to, totalRecords }) =>
                `Showing  ${from} to ${to} of ${totalRecords} entries`
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default SmlManager
